import React from "react";
import {useSettingsForm} from "./hooks";
import {Trans, useTranslation} from "react-i18next";
import {withErrorBoundary} from "../../components/ErrorBoundary";
import FormControlsButtons from "../../components/FormControlButtons";
import {CButton, CCard, CCardBody, CCardFooter, CCardHeader, CCol, CForm, CFormGroup, CLabel} from "@coreui/react";
import {ARInputCheckbox} from "../../components/ReactHookForm/fields/custom";
import {usePreventTransition} from "../../components/ReactHookForm/hooks";
import {ARInputWithLabel, ARTextareaWithLabel, ARPhoneInputWithLabel} from "../../components/ReactHookForm/fields";
import {showModalWindow, showNotification} from "../../utils/actionWrappers";
import {useSelector} from "react-redux";
import {telegramTokenIsSetSelector} from "../../store/selectors/global";
import {PATHS} from "../../route-dictionary";
import "./Settings.scss";

const Settings = () => {
    const {t} = useTranslation();
    const isTelegramTokenSet = useSelector(telegramTokenIsSetSelector);
    const {
      onFormSubmit,
      settingsInfo,
      resetTelegramToken,
      form
    } = useSettingsForm();
    const {handleSubmit, control, isDirty} = form;
    const {Prompt, redirect} = usePreventTransition(isDirty);
    const redirectToChecksPage = () => redirect(PATHS.CHECKS.ROOT);

    const onSubmit = (formData) => {
      if (isDirty) {
        onFormSubmit(formData)
          .then(() => showNotification(
            t("notification:title.success"),
            t("notification:message.updated.settings", "Settings have been updated."),
            "success")
          )
          .catch(({message}) => {
            if (message) {
              showNotification(t("notification:title.error", "Something went wrong!"), message, "danger");
            }
          });
      } else {
        redirectToChecksPage();
      }
    };

    const onResetTelegramTokenClick = () => {
      showModalWindow.confirm({
          message: t("modal:message.resetTelegramToken", "Are you sure you want to reset your Telegram token?"),
          color: "warning",
          onConfirm: () => {
            resetTelegramToken()
              .then(() => showNotification(
                t("notification:title.success"),
                t("notification:message.updated.settings", "Settings have been updated."),
                "success")
              );
          }
        }
      );
    };

    const onReset = (event) => {
      event.preventDefault();
      if (isDirty) {
        showModalWindow.confirm({
          message: t("modal:message.cancel", "Are you sure you want to cancel all the changes?"),
          color: "warning",
          onConfirm: redirectToChecksPage
        });
      } else {
        redirectToChecksPage();
      }
    };

    return (
      <CCard className="SettingsCard">
        <Prompt/>
        <CCardHeader>
          <h1 className="card-header-text">
            {t("form:section.company", "Company")} {settingsInfo.companyName}
          </h1>
        </CCardHeader>
        <CCardBody>
          <CForm
            onSubmit={handleSubmit(onSubmit)}
            id="settings-form"
          >
            <CFormGroup>
              <h2>Telegram</h2>
              <ARInputWithLabel
                id="telegram_connect_key"
                name="telegram_connect_key"
                labelText="Telegram Connect Key"
                placeholder={t("form:placeholder.default", "Enter Telegram Connect Key", {what: "Telegram Connect Key"})}
                control={control}
                autoComplete="off"
              />
              <ARInputWithLabel
                id="telegram_token"
                labelText={t("form:label.specificToken", "Telegram token", {what: "Telegram"})}
                name="telegram_token"
                control={control}
                placeholder={
                  isTelegramTokenSet ?
                    t("form:placeholder.newSpecificToken", "Enter new Telegram token", {what: "Telegram"}) :
                    t("form:placeholder.specificToken", "Enter Telegram token", {what: "Telegram"})
                }
                autoComplete="off"
              />
              <CFormGroup row className="SettingsCard__token-status-group">
                <CCol md="3">
                  <CLabel>
                    <Trans
                      i18nKey={`form:status.telegramToken_${isTelegramTokenSet ? "set" : "unset"}`}>
                      Telegram token is <b>{isTelegramTokenSet ? "SET" : "UNSET"}</b>
                    </Trans>
                  </CLabel>
                </CCol>
                {
                  isTelegramTokenSet &&
                  <CCol xs="12" md="9">
                    <CButton
                      color="danger"
                      variant="outline"
                      onClick={onResetTelegramTokenClick}
                      className="form-control-button"
                    >
                      {t("form:button.clearToken", "Clear token")}
                    </CButton>
                  </CCol>
                }
              </CFormGroup>
              {
                isTelegramTokenSet &&
                <ARInputCheckbox
                  id="is_telegram_auth_enabled"
                  name="is_telegram_auth_enabled"
                  control={control}
                  className="checkbox"
                  labelClassName="checkbox__label"
                  labelText={t("form:label.telegramAuth", "Enable Telegram authorization")}
                />
              }
            </CFormGroup>
            <CFormGroup>
              <h2>Nexmo</h2>
              <ARInputWithLabel
                id="nexmo_app_id"
                name="nexmo_app_id"
                labelText="App ID"
                placeholder={t("form:placeholder.default", "Enter App ID", {what: "App ID"})}
                control={control}
                autoComplete="off"
              />
              <ARPhoneInputWithLabel
                id="nexmo_from_phone"
                labelText={t("form:label.callFromPhone", "Call from phone")}
                name="nexmo_from_phone"
                control={control}
                autoComplete="off"
                block
              />
              <ARTextareaWithLabel
                id="nexmo_private_key"
                name="nexmo_private_key"
                labelText={t("form:label.privateKey", "Private key")}
                labelColClassName="form-textarea-label"
                placeholder={t("form:placeholder.privateKey", "Enter private key")}
                className="textarea-size"
                control={control}
                autoComplete="off"
              />
            </CFormGroup>
            <CFormGroup>
              <h2>Jira</h2>
              <ARInputWithLabel
                id="jira_token"
                name="jira_token"
                labelText={t("form:label.specificToken", "Jira token", {what: "Jira"})}
                placeholder={t("form:placeholder.specificToken", "Enter Jira token", {what: "Jira"})}
                control={control}
                autoComplete="off"
              />
              <ARInputWithLabel
                id="jira_url"
                name="jira_url"
                labelText="Jira URL"
                placeholder={t("form:placeholder.default", "Enter Jira URL", {what: "Jira URL"})}
                control={control}
                autoComplete="off"
              />
            </CFormGroup>
          </CForm>
        </CCardBody>
        <CCardFooter>
          <FormControlsButtons
            form="settings-form"
            onCancelClick={onReset}
          />
        </CCardFooter>
      </CCard>
    );
  }
;

export default withErrorBoundary(Settings);